import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { ButtonComponent, InfoBannerComponent, LoadingSpinnerComponent } from '@evc/web-components';
import {
  EditingService,
  ImageViewerComponent,
  ImageViewerConfigs,
  MeasureService,
  ThreejsViewerComponent,
  ThreejsViewerConfigs,
  ViewerError
} from "@evident/ngx-media-viewer";
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { InspectionImage } from '../../../_objects/inspection-image';
import { SpinnerService } from '../../../_services/spinner.service';
import { MeasurementPickerComponent } from './measurement-picker/measurement-picker.component';
import { MeasurementResultsComponent } from "./measurement-results/measurement-results.component";
import { MeasurementService } from './services/measurement.service';

@Component({
  selector: 'app-measurement-merry',
  templateUrl: './measurement-merry.component.html',
  styleUrl: './measurement-merry.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    ImageViewerComponent,
    LoadingSpinnerComponent,
    MeasurementPickerComponent,
    MeasurementResultsComponent,
    ThreejsViewerComponent,
    TranslateModule
  ]
})
export class MeasurementMerryComponent implements OnInit, OnDestroy {

  @Input({ required: true }) imageSrc!: string;
  @Input({ required: true }) inspectionImage!: InspectionImage;

  @ViewChild("threeDViewer")
  public threeDViewer!: ThreejsViewerComponent;

  @ViewChild("imageViewer")
  public imageViewer!: ImageViewerComponent;

  public isLoading3DModel = signal(false);
  public error = signal<string | null>(null);
  public localImageSrc: string;

  public readonly imageViewerTopConfigs: ImageViewerConfigs = {
    autoInitialize: true,
    maintainAspectRatio: true,
    maxZoom: 5,
    minZoom: 1,
    zoomSpeed: 0.05,
    zoomThrottle: 1,
    measurementStyles: {
      default: "green",
      highlighted: "blue",
      temporary: "red" ,
      reference: "orange"
    },
  };

  public readonly threejsViewerConfig: ThreejsViewerConfigs = {
    autoInitialize: true,
    fieldOfView: 70,
    nearClippingPane: 0.1,
    farClippingPane: 1000,
    defaultCameraZOffset: -20,
    defaultCameraMaxDistance: 2000,
    measurementStyles: {
      default: "green",
      highlighted: "blue",
      temporary: "red" ,
      reference: "orange"
    },
  };

  constructor(private measureService: MeasureService, private editingService: EditingService, private measurementService: MeasurementService, private spinnerService: SpinnerService) {
    this.measureService.initialize({ grpcUrl: environment.measurementServiceUrl });
  }

  ngOnDestroy(): void {
    this.resetEditingState();
  }

  async ngOnInit(): Promise<void> {
    this.localImageSrc = await this.measurementService.getLocalImageUrl(this.imageSrc, this.inspectionImage);
  }

  async onImageLoaded() {
    await this.generate3DModel();
    // We need to wait for the model 3D to load before retrieving Legacy Measurements (no Measurements 3D in legacy)
    await this.measurementService.retrieveExistingMeasurements(this.localImageSrc);
  }

  private resetEditingState() {
    this.editingService.measurements = [];
    this.editingService.temporaryMeasurement = null;
    this.editingService.areaReference = null;
    this.editingService.resetTemporaryMeasurements();
    this.editingService.reference = null;
    this.editingService.disable();
  }

  public async generate3DModel() {
    this.isLoading3DModel.set(true);
    await this.threeDViewer.loadFromImageSrc(this.localImageSrc);
    this.isLoading3DModel.set(false);
  }

  public onViewerError(error: ViewerError) {
    if (error) {
      this.isLoading3DModel.set(false);
      this.error.set("measurement.cannotReconstruct3D");
      console.error("Viewer error:", error);
    } else {
      console.warn("Viewer error event fired with null/undefined error");
    }
  }

  public async save() {
    if (this.editingService.selectedMeasurementType && this.editingService.temporaryMeasurement)
      await this.editingService.processMeasurement(this.editingService.temporaryMeasurement);

    this.spinnerService.show();
    const base64ImagePreview = this.imageViewer.exportImageWithMeasurements("image/jpeg");
    await this.measurementService.saveMeasurements(this.localImageSrc, this.inspectionImage, base64ImagePreview);
    this.spinnerService.hide();
  }

}
